import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/dealership/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MySignatureButton from "@/components/signatures/MySignatureButton";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import BusinessSwitcher from "@/components/BusinessSwitcher";
import {
  LayoutDashboard, Users, CalendarDays, Activity, Car, FileText, TrendingUp, ArrowLeft, LogOut, Menu, LineChart, ClipboardCheck, Calculator, Settings,
} from "lucide-react";

const navItems = [
  { to: "/dealership", label: "Dashboard", icon: LayoutDashboard, testid: "dealer-nav-dashboard", end: true },
  { to: "/dealership/leads", label: "Leads", icon: Users, testid: "dealer-nav-leads", leadsBadge: true },
  { to: "/dealership/activities", label: "Activities", icon: Activity, testid: "dealer-nav-activities", badge: true },
  { to: "/dealership/calendar", label: "Test Drives", icon: CalendarDays, testid: "dealer-nav-calendar" },
  { to: "/dealership/inventory", label: "Inventory", icon: Car, testid: "dealer-nav-inventory" },
  { to: "/dealership/pricing", label: "Price Matrix", icon: LineChart, testid: "dealer-nav-pricing", priceBadge: true },
  { to: "/dealership/appraisals", label: "Appraisals", icon: ClipboardCheck, testid: "dealer-nav-appraisals" },
  { to: "/dealership/desking", label: "Desking", icon: Calculator, testid: "dealer-nav-desking" },
  { to: "/dealership/deals", label: "Deals & Bills", icon: FileText, testid: "dealer-nav-deals" },
  { to: "/dealership/financials", label: "Profit & Costs", icon: TrendingUp, testid: "dealer-nav-financials" },
  { to: "/dealership/settings", label: "Settings", icon: Settings, testid: "dealer-nav-settings" },
];

export default function DealerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: notifCount } = useQuery({
    queryKey: ["dealer-notif-count"],
    queryFn: async () => (await api.get("/notifications/count")).data,
    refetchInterval: 60000,
  });
  const dueCount = notifCount?.count || 0;

  const { data: newLeadCount = 0 } = useQuery({
    queryKey: ["dealer-new-lead-count"],
    queryFn: async () => (await api.get("/leads/new-count")).data.count,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const { data: priceNotif } = useQuery({
    queryKey: ["price-notif-count"],
    queryFn: async () => (await api.get("/pricing/notifications/count")).data,
    refetchInterval: 60000,
  });
  const priceCount = priceNotif?.count || 0;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors duration-150 ${
      isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"
    }`;

  const SidebarBody = ({ onNavigate }) => (
    <>
      <div className="px-5 py-6 border-b border-border">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-primary/12 border border-primary/25 flex items-center justify-center shrink-0">
            <Car className="h-5 w-5 text-primary" />
          </span>
          <div className="min-w-0">
            <div className="font-heading text-lg leading-tight truncate">Headon Auto</div>
            <div className="label-mono mt-0.5">Dealership</div>
          </div>
        </div>
      </div>
      <div className="px-3 pt-3">
        <NavLink to="/" onClick={onNavigate} data-testid="dealer-back-to-rental" className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-muted-foreground hover:bg-white/[0.04] hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Rental CRM
        </NavLink>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} data-testid={item.testid} className={linkClass}>
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
            {item.badge && dueCount > 0 && (
              <span className="ml-auto bg-destructive text-white text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center font-mono" data-testid="dealer-activities-badge">
                {dueCount}
              </span>
            )}
            {item.leadsBadge && newLeadCount > 0 && (
              <span className="ml-auto bg-red-600 text-white text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center font-bold animate-pulse" data-testid="dealer-leads-new-badge" title={`${newLeadCount} new email lead(s) awaiting reply`}>
                {newLeadCount > 99 ? "99+" : newLeadCount}
              </span>
            )}
            {item.priceBadge && priceCount > 0 && (
              <span className="ml-auto bg-amber-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center font-bold" data-testid="dealer-price-alerts-badge" title={`${priceCount} price alert(s)`}>
                {priceCount > 99 ? "99+" : priceCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-border">
        <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
          <span className="w-9 h-9 rounded-full bg-primary/15 border border-primary/25 text-primary flex items-center justify-center font-heading text-sm shrink-0">
            {(user?.name || "?").trim().charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm truncate" data-testid="dealer-current-user-name">{user?.name}</p>
            <p className="label-mono mt-0.5">{user?.role}</p>
          </div>
          <MySignatureButton />
          <button onClick={handleLogout} data-testid="dealer-logout-button" title="Sign out" aria-label="Sign out"
            className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/[0.06] transition-colors shrink-0">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background grain-overlay">
      <aside className="w-64 shrink-0 bg-card/40 border-r border-border hidden md:flex flex-col fixed h-screen overflow-y-auto">
        <SidebarBody />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-card flex flex-col md:hidden" data-testid="dealer-mobile-sidebar">
          <SheetTitle className="sr-only">Dealership navigation</SheetTitle>
          <SidebarBody onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <main className="flex-1 md:ml-64 min-h-screen min-w-0 relative z-10">
        <header className="hidden md:flex sticky top-0 z-30 bg-background/70 backdrop-blur-xl border-b border-border items-center gap-3 px-6 h-16">
          <BusinessSwitcher />
        </header>
        <header className="md:hidden sticky top-0 z-30 bg-background/70 backdrop-blur-xl border-b border-border flex items-center gap-3 px-4 h-16">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="dealer-mobile-menu" aria-label="Open menu"><Menu className="h-5 w-5" /></Button>
          <span className="font-heading text-base flex items-center gap-2"><Car className="h-5 w-5 text-primary" />Headon Auto</span>
          <div className="ml-auto"><BusinessSwitcher /></div>
        </header>
        <Outlet />
      </main>
    </div>
  );
}
